import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  ChevronLeft, TrendingUp, Tv, Users, IndianRupee,
  Calendar, Loader2, Wallet, BarChart3,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const RS = (n) => `Rs.${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

export default function PartnerCommissions() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const basePath = window.location.pathname.startsWith('/partner') ? '/partner' : '/portal';
  const token = localStorage.getItem('portal_token');

  useEffect(() => {
    if (!token) { navigate(basePath); return; }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/portal/lco/commission-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403) {
        toast.error('Not authorized. Please log in as a Partner.');
        navigate(basePath);
        return;
      }
      const d = await res.json();
      setData(d);
    } catch (e) { toast.error('Failed to load commission data'); }
    setLoading(false);
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const lifetime = data.lifetime || {};
  const thisMonth = data.this_month || {};
  const monthly = data.monthly_series || [];
  const byLco = data.by_lco || [];
  const maxMonth = Math.max(1, ...monthly.map(m => m.total || 0));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="commissions-page">
      <header className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(`${basePath}/dashboard`)} className="p-1 hover:bg-slate-100 rounded-md">
            <ChevronLeft className="h-5 w-5 text-slate-600" />
          </button>
          <img src="/logo.png" alt="Logo" className="h-8 w-auto" />
          <div className="flex-1">
            <h1 className="font-bold text-lg text-slate-900 tracking-tight flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-amber-600" /> Commission Dashboard
            </h1>
            <p className="text-xs text-slate-500">OTT + IPTV earnings from your LCOs</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate(`${basePath}/lcos`)} data-testid="goto-lcos-btn">
            <Users className="h-4 w-4 mr-1" /> My LCOs
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 space-y-6">
        {/* Top KPI cards */}
        <div className="grid gap-3 md:grid-cols-4">
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white" data-testid="kpi-lifetime">
            <CardContent className="p-4">
              <p className="text-amber-100 text-xs flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Lifetime</p>
              <p className="text-2xl font-bold mt-1">{RS(lifetime.total_commission)}</p>
              <p className="text-xs text-amber-100 mt-1">{lifetime.total_activations || 0} activations</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white" data-testid="kpi-this-month">
            <CardContent className="p-4">
              <p className="text-emerald-100 text-xs flex items-center gap-1"><Calendar className="h-3 w-3" /> This Month</p>
              <p className="text-2xl font-bold mt-1">{RS(thisMonth.total_commission)}</p>
              <p className="text-xs text-emerald-100 mt-1">{thisMonth.total_activations || 0} activations</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white" data-testid="kpi-ott-wallet">
            <CardContent className="p-4">
              <p className="text-slate-400 text-xs flex items-center gap-1"><Wallet className="h-3 w-3" /> OTT Wallet</p>
              <p className="text-2xl font-bold mt-1">{RS(data.ott_wallet_balance)}</p>
              <p className="text-xs text-slate-400 mt-1">Partner balance</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-indigo-900 to-indigo-800 text-white" data-testid="kpi-iptv-wallet">
            <CardContent className="p-4">
              <p className="text-indigo-200 text-xs flex items-center gap-1"><Tv className="h-3 w-3" /> IPTV Wallet</p>
              <p className="text-2xl font-bold mt-1">{RS(data.iptv_wallet_balance)}</p>
              <p className="text-xs text-indigo-200 mt-1">Partner balance</p>
            </CardContent>
          </Card>
        </div>

        {/* OTT vs IPTV split */}
        <div className="grid gap-3 md:grid-cols-2">
          <Card data-testid="ott-summary-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-slate-700">
                <Tv className="h-4 w-4" /> OTT Commission
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Lifetime</span><span className="font-bold text-amber-600">{RS(lifetime.ott?.total_commission)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">This Month</span><span className="font-semibold">{RS(thisMonth.ott?.total_commission)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Activations</span><span>{lifetime.ott?.total_activations || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Revenue Processed</span><span>{RS(lifetime.ott?.total_revenue)}</span></div>
            </CardContent>
          </Card>
          <Card data-testid="iptv-summary-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-slate-700">
                <Tv className="h-4 w-4" /> IPTV Commission
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Lifetime</span><span className="font-bold text-amber-600">{RS(lifetime.iptv?.total_commission)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">This Month</span><span className="font-semibold">{RS(thisMonth.iptv?.total_commission)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Subscriptions</span><span>{lifetime.iptv?.total_activations || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Revenue Processed</span><span>{RS(lifetime.iptv?.total_revenue)}</span></div>
            </CardContent>
          </Card>
        </div>

        {/* Monthly series */}
        <Card data-testid="monthly-chart-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4" /> Last 6 Months
            </CardTitle>
          </CardHeader>
          <CardContent>
            {monthly.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No data yet</p>
            ) : (
              <div className="space-y-3">
                {monthly.map(m => (
                  <div key={m.month} className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="font-medium text-slate-700">{m.month}</span>
                      <span className="text-slate-500">
                        OTT {RS(m.ott)} • IPTV {RS(m.iptv)} = <span className="font-semibold text-amber-600">{RS(m.total)}</span>
                      </span>
                    </div>
                    <div className="flex gap-0.5 h-4 rounded overflow-hidden bg-slate-100">
                      <div
                        className="bg-emerald-500 transition-all"
                        style={{ width: `${(m.ott / maxMonth) * 100}%` }}
                        title={`OTT ${RS(m.ott)}`}
                      />
                      <div
                        className="bg-indigo-500 transition-all"
                        style={{ width: `${(m.iptv / maxMonth) * 100}%` }}
                        title={`IPTV ${RS(m.iptv)}`}
                      />
                    </div>
                  </div>
                ))}
                <div className="flex gap-4 text-xs text-slate-500 mt-2">
                  <span className="flex items-center gap-1"><span className="h-2 w-2 bg-emerald-500 rounded-sm" /> OTT</span>
                  <span className="flex items-center gap-1"><span className="h-2 w-2 bg-indigo-500 rounded-sm" /> IPTV</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* By-LCO breakdown */}
        <Card data-testid="by-lco-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" /> By LCO (Top 20)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {byLco.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">No commission-generating LCOs yet</div>
            ) : (
              <div className="divide-y">
                {byLco.map(l => (
                  <div key={l.lco_customer_id} className="p-4 flex items-center justify-between gap-3" data-testid={`lco-row-${l.lco_customer_id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 truncate">{l.lco_name}</p>
                      <p className="text-xs text-slate-500">{l.lco_phone || '—'}</p>
                      <div className="flex gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs">OTT: {l.ott_count || 0}</Badge>
                        <Badge variant="secondary" className="text-xs">IPTV: {l.iptv_count || 0}</Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Earned</p>
                      <p className="font-bold text-amber-600">{RS(l.total_commission)}</p>
                      <p className="text-xs text-slate-400">OTT {RS(l.ott_commission)} • IPTV {RS(l.iptv_commission)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
